import type { TranslationBundle } from '@bms/common';

/**
 * HotelSettingsPage — Access hours and Occupancy automation.
 *
 * The `frontOffice*` keys below back `./frontOffice/frontOfficeView.ts`'s
 * `describeWindow`, `scheduleWindowsIssue`, `occupancyDraftIssue` and
 * `occupancyReadOnlyReason`, which take a `tr: Translate` (default English) the
 * way Admin's `model/translate.ts` does, since that file is a pure view model
 * with no React/i18next import of its own. Weekday names are not here —
 * `frontOfficeView.weekdays(locale)` names them through `Intl.DateTimeFormat`.
 */
export const hotelSettings: TranslationBundle = {
  en: {
    noSiteTitle: 'No site selected',
    noSiteDescription: 'Choose a site to change its hotel policy.',
    notAvailableTitle: 'Hotel settings is not available to this account',
    notAvailableDescription: 'This account cannot change access hours or the occupancy settings at this site. Ask a site administrator for access.',
    storedChanged: 'These settings were saved elsewhere while you were editing. Your unsaved changes are kept.',
    loadStored: 'Load the saved settings',
    switchTitle: 'Discard unsaved hours?',
    switchDescription: 'Your changes to {{schedule}} are not saved. Opening {{next}} discards them.',
    switchConfirm: 'Discard and open',
    activeSeason: 'Setpoints for {{mode}} mode. The site administrator selects the season in HVAC settings.',
    heating: 'heating', cooling: 'cooling',
    subtitle: 'When access applies, and what a room does when a guest arrives or leaves.',
    sectionAria: 'Hotel settings section',
    sectionAccessHours: 'Access hours',
    sectionOccupancy: 'Occupancy automation',

    windowNeedsOneTitle: 'A schedule needs at least one window',
    removeWindowAria: 'Remove window {{index}}',
    daysOfWindowAria: 'Days of window {{index}}',
    opensLabel: 'Opens',
    closesLabel: 'Closes',

    noAccessSchedulesTitle: 'No access schedules',
    noAccessSchedulesDescription: 'A technician creates and attaches schedules in Admin. Once one exists, its hours can be changed here.',
    notApplied: 'Not applied',
    noHoursSet: 'No hours set',
    lockAttachmentCount_one: '{{count}} door',
    lockAttachmentCount_other: '{{count}} doors',
    cardAttachmentCount_one: '{{count}} card',
    cardAttachmentCount_other: '{{count}} cards',
    attachmentSummary: '{{doors}} · {{cards}}',
    scheduleOwnershipHint: 'Attached doors, the schedule’s name and whether it is applied are set by a technician in Admin.',
    addWindow: 'Add a window',
    windowLimitTitle: 'A schedule holds at most {{max}} windows',
    saving: 'Saving…',
    saveHours: 'Save hours',
    discard: 'Discard',
    windowWrapHint: 'A window that ends before it starts runs into the next day; one whose start and end are the same is open all day. Neither is a mistake.',
    toastHoursUpdated: 'Hours updated for {{schedule}}',
    toastHoursSaveFailed: 'The hours could not be saved',

    checkInTitle: 'When a guest checks in',
    vacantTitle: 'When a room falls vacant',
    comfortSetpointLabel: 'Comfort setpoint (°C)',
    ecoSetpointLabel: 'Eco setpoint (°C)',
    welcomeSceneLabel: 'Welcome scene',
    lightsOffSceneLabel: 'Lights-off scene',
    noScene: 'No scene',
    sceneDefinedInNone: '— defined in no sampled room',
    sceneDefinedInRooms_one: '— {{count}} room',
    sceneDefinedInRooms_other: '— {{count}} rooms',
    occupancyDisabledHint: 'Occupancy automation is switched off for this site, so these values are stored but not acted on. Switching it on is done by a site administrator in Admin under Settings › Hotel policy.',
    saveSetpoints: 'Save setpoints',
    checkoutTimeTitle: 'House checkout time',
    checkoutTimeLabel: 'Guests check out by',
    checkoutTimeHint: 'Check-in counts nights to this time on the departure day, in the hotel’s time zone. Without a setting it is 11:00.',
    saveCheckoutTime: 'Save checkout time',
    toastCheckoutTimeUpdated: 'Checkout time updated',
    toastCheckoutTimeSaveFailed: 'The checkout time could not be saved',
    toastOccupancyUpdated: 'Occupancy setpoints updated',
    toastOccupancySaveFailed: 'The setpoints could not be saved',
    setpointRangeHint_one: 'Setpoints are between {{min}} and {{max}} °C. Scene codes are offered from the scenes the first {{sampled}} room of this site define; a scene missing from a room simply does not run there.',
    setpointRangeHint_other: 'Setpoints are between {{min}} and {{max}} °C. Scene codes are offered from the scenes the first {{sampled}} rooms of this site define; a scene missing from a room simply does not run there.',

    // frontOfficeView.ts — scheduleWindowsIssue
    frontOfficeIssueEmptyList: 'A schedule needs at least one window. To close this door entirely, a technician disables or detaches the schedule.',
    frontOfficeIssueTooMany: 'A schedule holds at most {{max}} windows.',
    frontOfficeIssueNoDays: 'Window {{index}} needs at least one day.',
    frontOfficeIssueBadStart: 'Window {{index}} has an invalid start time.',
    frontOfficeIssueBadEnd: 'Window {{index}} has an invalid end time.',

    // frontOfficeView.ts — describeWindow
    frontOfficeEveryDay: 'Every day',
    frontOfficeWindowAllDay: '{{days}} · all day',
    frontOfficeWindowRange: '{{days}} · {{start}}–{{end}}',
    frontOfficeWindowRangeNextDay: '{{days}} · {{start}}–{{end}} (next day)',

    // frontOfficeView.ts — occupancyDraftIssue
    frontOfficeCheckInComfortLabel: 'The check-in comfort setpoint',
    frontOfficeVacancyEcoLabel: 'The vacancy eco setpoint',
    frontOfficeWelcomeSceneFieldLabel: 'The welcome scene',
    frontOfficeLightsOffSceneFieldLabel: 'The lights-off scene',
    frontOfficeIssueMustBeNumber: '{{label}} must be a number.',
    frontOfficeIssueMustBeBetween: '{{label}} must be between {{min}} and {{max}} °C.',
    frontOfficeIssueSceneCode: '{{label}} must be a scene code: lower-case letters, digits, hyphen or underscore.',

    // frontOfficeView.ts — occupancyReadOnlyReason
    frontOfficeNotConfigured: 'Occupancy automation is not configured for this site. A site administrator sets it up in Admin under Settings › Hotel policy.',
    frontOfficeNeedsWritePermission: 'This account cannot change these values. Ask a site administrator for access.',
  },
  hr: {
    activeSeason: 'Zadane temperature za način {{mode}}. Administrator odabire sezonu u postavkama HVAC-a.',
    heating: 'grijanje', cooling: 'hlađenje',
    noSiteTitle: 'Nije odabrana lokacija',
    noSiteDescription: 'Odaberite lokaciju da biste promijenili njezinu hotelsku politiku.',
    notAvailableTitle: 'Postavke hotela nisu dostupne ovom računu',
    notAvailableDescription: 'Ovaj račun na ovoj lokaciji ne može mijenjati vrijeme pristupa ni postavke zauzetosti. Zamolite administratora lokacije za pristup.',
    storedChanged: 'Ove su postavke spremljene negdje drugdje dok ste ih uređivali. Vaše nespremljene promjene su zadržane.',
    loadStored: 'Učitaj spremljene postavke',
    switchTitle: 'Odbaciti nespremljeno vrijeme?',
    switchDescription: 'Promjene za {{schedule}} nisu spremljene. Otvaranje rasporeda {{next}} ih odbacuje.',
    switchConfirm: 'Odbaci i otvori',
    subtitle: 'Kada pristup vrijedi i što soba radi kad gost dođe ili ode.',
    sectionAria: 'Odjeljak postavki hotela',
    sectionAccessHours: 'Pristupno vrijeme',
    sectionOccupancy: 'Automatizacija zauzetosti',

    windowNeedsOneTitle: 'Raspored treba barem jedno razdoblje',
    removeWindowAria: 'Ukloni razdoblje {{index}}',
    daysOfWindowAria: 'Dani razdoblja {{index}}',
    opensLabel: 'Otvara se',
    closesLabel: 'Zatvara se',

    noAccessSchedulesTitle: 'Nema rasporeda pristupa',
    noAccessSchedulesDescription: 'Tehničar stvara i pridružuje rasporede u Adminu. Kad raspored postoji, njegovo vrijeme se može mijenjati ovdje.',
    notApplied: 'Nije primijenjen',
    noHoursSet: 'Vrijeme nije postavljeno',
    lockAttachmentCount_one: '{{count}} vrata',
    lockAttachmentCount_few: '{{count}} vrata',
    lockAttachmentCount_other: '{{count}} vrata',
    cardAttachmentCount_one: '{{count}} kartica',
    cardAttachmentCount_few: '{{count}} kartice',
    cardAttachmentCount_other: '{{count}} kartica',
    attachmentSummary: '{{doors}} · {{cards}}',
    scheduleOwnershipHint: 'Pridružena vrata, naziv rasporeda i je li primijenjen postavlja tehničar u Adminu.',
    addWindow: 'Dodaj razdoblje',
    windowLimitTitle: 'Raspored može sadržavati najviše {{max}} razdoblja',
    saving: 'Spremanje…',
    saveHours: 'Spremi vrijeme',
    discard: 'Odbaci',
    windowWrapHint: 'Razdoblje koje završava prije nego što počne prelazi u sljedeći dan; ono kojemu su početak i kraj isti otvoreno je cijeli dan. Nijedno od toga nije pogreška.',
    toastHoursUpdated: 'Vrijeme ažurirano za {{schedule}}',
    toastHoursSaveFailed: 'Vrijeme nije moguće spremiti',

    checkInTitle: 'Kad se gost prijavi',
    vacantTitle: 'Kad soba postane prazna',
    comfortSetpointLabel: 'Zadana udobna temperatura (°C)',
    ecoSetpointLabel: 'Zadana eko temperatura (°C)',
    welcomeSceneLabel: 'Scena dobrodošlice',
    lightsOffSceneLabel: 'Scena gašenja svjetla',
    noScene: 'Bez scene',
    sceneDefinedInNone: '— nije definirana ni u jednoj uzorkovanoj sobi',
    sceneDefinedInRooms_one: '— {{count}} soba',
    sceneDefinedInRooms_few: '— {{count}} sobe',
    sceneDefinedInRooms_other: '— {{count}} soba',
    occupancyDisabledHint: 'Automatizacija zauzetosti isključena je za ovu lokaciju, pa su ove vrijednosti pohranjene ali se ne primjenjuju. Uključivanje obavlja administrator lokacije u Adminu pod Postavke › Hotelska politika.',
    saveSetpoints: 'Spremi zadane vrijednosti',
    checkoutTimeTitle: 'Vrijeme odjave hotela',
    checkoutTimeLabel: 'Gosti se odjavljuju do',
    checkoutTimeHint: 'Prijava broji noćenja do ovog vremena na dan odlaska, u vremenskoj zoni hotela. Bez postavke to je 11:00.',
    saveCheckoutTime: 'Spremi vrijeme odjave',
    toastCheckoutTimeUpdated: 'Vrijeme odjave ažurirano',
    toastCheckoutTimeSaveFailed: 'Vrijeme odjave nije moguće spremiti',
    toastOccupancyUpdated: 'Zadane vrijednosti zauzetosti ažurirane',
    toastOccupancySaveFailed: 'Zadane vrijednosti nije moguće spremiti',
    setpointRangeHint_one: 'Zadane vrijednosti su između {{min}} i {{max}} °C. Kodovi scena nude se iz scena koje definira prva {{sampled}} soba ove lokacije; scena koja nedostaje u sobi jednostavno se ondje ne pokreće.',
    setpointRangeHint_few: 'Zadane vrijednosti su između {{min}} i {{max}} °C. Kodovi scena nude se iz scena koje definiraju prve {{sampled}} sobe ove lokacije; scena koja nedostaje u sobi jednostavno se ondje ne pokreće.',
    setpointRangeHint_other: 'Zadane vrijednosti su između {{min}} i {{max}} °C. Kodovi scena nude se iz scena koje definira prvih {{sampled}} soba ove lokacije; scena koja nedostaje u sobi jednostavno se ondje ne pokreće.',

    // frontOfficeView.ts — scheduleWindowsIssue
    frontOfficeIssueEmptyList: 'Raspored treba barem jedno razdoblje. Da biste posve zatvorili ova vrata, tehničar onemogućuje ili odvaja raspored.',
    frontOfficeIssueTooMany: 'Raspored može sadržavati najviše {{max}} razdoblja.',
    frontOfficeIssueNoDays: 'Razdoblje {{index}} treba barem jedan dan.',
    frontOfficeIssueBadStart: 'Razdoblje {{index}} ima nevažeće vrijeme početka.',
    frontOfficeIssueBadEnd: 'Razdoblje {{index}} ima nevažeće vrijeme završetka.',

    // frontOfficeView.ts — describeWindow
    frontOfficeEveryDay: 'Svaki dan',
    frontOfficeWindowAllDay: '{{days}} · cijeli dan',
    frontOfficeWindowRange: '{{days}} · {{start}}–{{end}}',
    frontOfficeWindowRangeNextDay: '{{days}} · {{start}}–{{end}} (sljedeći dan)',

    // frontOfficeView.ts — occupancyDraftIssue
    frontOfficeCheckInComfortLabel: 'Zadana udobna temperatura pri prijavi',
    frontOfficeVacancyEcoLabel: 'Zadana eko temperatura pri praznoj sobi',
    frontOfficeWelcomeSceneFieldLabel: 'Scena dobrodošlice',
    frontOfficeLightsOffSceneFieldLabel: 'Scena gašenja svjetla',
    frontOfficeIssueMustBeNumber: '{{label}} mora biti broj.',
    frontOfficeIssueMustBeBetween: '{{label}} mora biti između {{min}} i {{max}} °C.',
    frontOfficeIssueSceneCode: '{{label}} mora biti kod scene: mala slova, brojevi, crtica ili donja crta.',

    // frontOfficeView.ts — occupancyReadOnlyReason
    frontOfficeNotConfigured: 'Automatizacija zauzetosti nije konfigurirana za ovu lokaciju. Administrator lokacije postavlja je u Adminu pod Postavke › Hotelska politika.',
    frontOfficeNeedsWritePermission: 'Ovaj račun ne može mijenjati ove vrijednosti. Zamolite administratora lokacije za pristup.',
  },
};
